import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TagPoolField } from "@/components/tags/TagPoolField";
import { useToast } from "@/hooks/use-toast";
import companyService from "@/services/company";
import { Company, CompanyFormData } from "@/types/company";
import { useState } from "react";

interface CompanyFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: Company;
  mode: "create" | "edit";
}

export default function CompanyForm({ open, onClose, onSuccess, initialData, mode }: CompanyFormProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<CompanyFormData>(
    initialData
      ? { name: initialData.name, isActive: initialData.isActive, tags: initialData.tags || [] }
      : { name: "", isActive: true, tags: [] }
  );
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast({ title: "Erro", description: "Nome da empresa é obrigatório", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData };
      const [, error] =
        mode === "create"
          ? await companyService.create(payload)
          : await companyService.update(initialData!._id, payload);

      if (error) {
        toast({
          title: "Erro",
          description: error.msg || "Erro ao salvar empresa",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Sucesso",
        description: mode === "create" ? "Empresa criada com sucesso" : "Empresa atualizada com sucesso",
      });
      onSuccess();
      onClose();
    } catch {
      toast({ title: "Erro", description: "Ocorreu um erro ao processar a requisição", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{mode === "create" ? "Nova Empresa" : "Editar Empresa"}</DialogTitle>
            <DialogDescription>
              {mode === "create" ? "Preencha os dados da nova empresa" : "Atualize os dados da empresa"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Nome
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Ex.: Docol"
              />
            </div>
            <TagPoolField
              id="company-tags"
              value={formData.tags || []}
              onChange={(tags) => setFormData((prev) => ({ ...prev, tags }))}
            />
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label htmlFor="active" className="text-sm font-medium">
                Ativa
              </Label>
              <div className="flex items-center gap-2">
                <Switch
                  id="active"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isActive: checked }))}
                />
                <span className="text-sm text-muted-foreground">{formData.isActive ? "Sim" : "Não"}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Salvando..." : mode === "create" ? "Criar" : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
